import { useState, useEffect, useCallback, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
// Frameworks
import {
  Box,
  Divider,
  Typography,
  Modal,
  Fade,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import useAppDispatch from "hooks/useAppDispatch";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "react-i18next";
// Custom imports
import { show } from "store/reducers/notifications.reducer";
// Assets
import Logo from "assets/img/logo.png";
import LoginIcon from "@mui/icons-material/Login";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import customAxios from "lib/axios";

const POPUP_PENDING_KEY = "tradeasy_referral_popup_pending";
const GOOGLE_SCRIPT_ID = "google-identity-services-script";

const GoogleLogo = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18,
      height: 18,
    }}
  >
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9c.87-2.6 3.3-4.52 6.16-4.52z"
      />
    </svg>
  </Box>
);

type GoogleTokenResponse = {
  access_token?: string;
  error?: string;
};

type GoogleTokenClient = {
  requestAccessToken: () => void;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (options: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
          }) => GoogleTokenClient;
        };
      };
    };
  }
}

const Login = function () {
  const localUserId = localStorage.getItem("user_id");
  const dispatch = useAppDispatch();
  const isUserLoggedIn =
    localUserId && !Number.isNaN(localStorage.getItem("user_id"));
  const [t] = useTranslation(["login"]);
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleButtonError, setGoogleButtonError] = useState(false);
  const [googleLoadRetry, setGoogleLoadRetry] = useState(0);
  const googleTokenClientRef = useRef<GoogleTokenClient | null>(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const showGoogleConfigWarning =
    !googleClientId &&
    (process.env.NODE_ENV !== "production" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const completeLogin = useCallback(
    (userHash: string, email?: string) => {
      dispatch(show({ type: "success", msg: t("sesionOK") }));
      localStorage.setItem("user_id", userHash);
      if (email) {
        localStorage.setItem("user_email", email.trim().toLowerCase());
      }
      sessionStorage.setItem(POPUP_PENDING_KEY, "1");
      setOpen(false);
      window.location.reload();
    },
    [dispatch, t]
  );

  const completeGoogleLogin = useCallback(
    async (userHash: string, email?: string) => {
      localStorage.setItem("user_id", userHash);
      if (email) {
        localStorage.setItem("user_email", email.trim().toLowerCase());
      }

      const { data } = await customAxios.post("/users/validateHash", {});

      if (data !== true) {
        localStorage.removeItem("user_id");
        localStorage.removeItem("user_email");
        localStorage.removeItem("type_user");
        localStorage.removeItem("user_valid_count");
        dispatch(show({ type: "error", msg: t("googleLoginError") }));
        return;
      }

      dispatch(show({ type: "success", msg: t("sesionOK") }));
      sessionStorage.setItem(POPUP_PENDING_KEY, "1");
      setOpen(false);
      window.location.reload();
    },
    [dispatch, t]
  );

  const onSubmit = () => {
    // Test mode for front-end development
    // setLoading(true);
    // dispatch(
    //   show({ type: "success", msg: "Sesión iniciada correctamente" })
    // );
    // localStorage.setItem("user_id", "res.data.user_id");
    // setOpen(false);
    // window.location.reload();
    // return;

    customAxios
      .post("/users/login", { email: userName, password })
      .then((res) => {
        if (res.data.success) {
          completeLogin(res.data.user_id, userName);
        } else {
          localStorage.removeItem("user_id");
          localStorage.removeItem("user_email");
          localStorage.removeItem("type_user");
          localStorage.removeItem("user_valid_count");
          dispatch(show({ type: "error", msg: t("incorrectUserPass") }));
        }
      })
      .catch((e) => {
        console.error(e);
        dispatch(show({ type: "error", msg: t("unexpectedError") }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const submitGoogleLogin = useCallback(
    (payload: { credential?: string; accessToken?: string }) => {
      setGoogleLoading(true);
      customAxios
        .post("/users/login/google", payload)
        .then((res) => {
          const sessionToken = res.data.user_id || res.data.hash_user_id;

          if (res.data.success && sessionToken) {
            return completeGoogleLogin(sessionToken, res.data.email);
          } else {
            dispatch(show({ type: "error", msg: t("googleLoginError") }));
          }

          return null;
        })
        .catch((error) => {
          console.error(error);
          dispatch(show({ type: "error", msg: t("googleLoginError") }));
        })
        .finally(() => {
          setGoogleLoading(false);
        });
    },
    [completeGoogleLogin, dispatch, t]
  );

  const handleGoogleAccessToken = useCallback(
    (response: GoogleTokenResponse) => {
      if (response.error || !response.access_token) {
        console.error("Google OAuth token error", response);
        dispatch(show({ type: "error", msg: t("googleLoginError") }));
        return;
      }

      submitGoogleLogin({ accessToken: response.access_token });
    },
    [dispatch, submitGoogleLogin, t]
  );

  const initializeGoogleTokenClient = useCallback(() => {
    if (!googleClientId || !window.google?.accounts?.oauth2?.initTokenClient) {
      return false;
    }

    googleTokenClientRef.current =
      window.google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        scope: "openid email profile",
        callback: handleGoogleAccessToken,
      });
    setGoogleReady(true);
    setGoogleButtonError(false);
    return true;
  }, [googleClientId, handleGoogleAccessToken]);

  const requestGoogleAccess = useCallback(() => {
    if (!googleTokenClientRef.current && !initializeGoogleTokenClient()) {
      setGoogleButtonError(true);
      dispatch(show({ type: "error", msg: t("googleLoginError") }));
      return;
    }

    googleTokenClientRef.current?.requestAccessToken();
  }, [dispatch, initializeGoogleTokenClient, t]);

  const retryGoogleLoad = useCallback(() => {
    googleTokenClientRef.current = null;
    setGoogleReady(false);
    setGoogleButtonError(false);
    setGoogleLoadRetry((retry) => retry + 1);
  }, []);

  useEffect(() => {
    if (!open || !googleClientId) return;

    let cancelled = false;
    let googleLoadAttempts = 0;
    let googleLoadTimer: ReturnType<typeof setInterval> | undefined;
    let script = document.getElementById(
      GOOGLE_SCRIPT_ID
    ) as HTMLScriptElement | null;

    googleTokenClientRef.current = null;
    setGoogleReady(false);
    setGoogleButtonError(false);

    const tryInitializeGoogle = () => {
      if (cancelled) return;

      if (initializeGoogleTokenClient()) {
        if (googleLoadTimer) clearInterval(googleLoadTimer);
        return;
      }

      googleLoadAttempts += 1;
      if (googleLoadAttempts >= 50) {
        if (googleLoadTimer) clearInterval(googleLoadTimer);
        console.warn("Google Identity Services script did not initialize.", {
          origin: window.location.origin,
          hasScript: Boolean(document.getElementById(GOOGLE_SCRIPT_ID)),
          hasGoogle: Boolean(window.google),
          hasOauth2: Boolean(window.google?.accounts?.oauth2),
        });
        setGoogleButtonError(true);
      }
    };

    const handleScriptError = () => {
      if (!cancelled) {
        console.error("Google Identity Services script failed to load.", {
          origin: window.location.origin,
          scriptSrc: script?.src,
        });
        setGoogleButtonError(true);
        dispatch(show({ type: "error", msg: t("googleLoginError") }));
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.id = GOOGLE_SCRIPT_ID;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.addEventListener("load", tryInitializeGoogle);
      script.addEventListener("error", handleScriptError);
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", tryInitializeGoogle);
      script.addEventListener("error", handleScriptError);
    }

    googleLoadTimer = setInterval(tryInitializeGoogle, 200);
    tryInitializeGoogle();

    return () => {
      cancelled = true;
      if (googleLoadTimer) clearInterval(googleLoadTimer);
      googleTokenClientRef.current = null;
      script?.removeEventListener("load", tryInitializeGoogle);
      script?.removeEventListener("error", handleScriptError);
    };
  }, [
    dispatch,
    googleClientId,
    googleLoadRetry,
    initializeGoogleTokenClient,
    open,
    t,
  ]);

  useEffect(() => {
    function onEnter(event: KeyboardEvent) {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        onSubmit();
      }
    }

    if (isUserLoggedIn) {
      document.removeEventListener("keydown", onEnter);
      handleClose();
    } else {
      document.addEventListener("keydown", onEnter);
      handleOpen();
    }

    return () => {
      document.removeEventListener("keydown", onEnter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, password, isUserLoggedIn]);

  return (
    <Modal
      open={open}
      disableAutoFocus
      disableRestoreFocus
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(6px)",
      }}
    >
      <Fade in={open}>
        <Box
          bgcolor="#1A2B47"
          px={4}
          py={1}
          borderRadius="10px"
          display="flex"
          flexDirection="column"
        >
          <Box display="flex" justifyContent="center" my={4}>
            <img src={Logo} alt="tradEAsy Logo" width="200px" />
          </Box>

          <TextField
            label={t("email")}
            color="secondary"
            type="email"
            variant="outlined"
            name="username"
            onChange={(e) => setUserName(e.target.value)}
            value={userName}
            sx={{ my: 2 }}
            InputProps={{
              sx: { color: "secondary.main" },
            }}
            InputLabelProps={{ sx: { color: "secondary.main" } }}
          />

          <TextField
            label={t("password")}
            type={showPassword ? "text" : "password"}
            variant="outlined"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            color="secondary"
            InputProps={{
              sx: { color: "secondary.main" },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    color="secondary"
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ sx: { color: "secondary.main" } }}
          />
          <Link
            component={RouterLink}
            to="/forgot-password"
            variant="body2"
            sx={{ cursor: "pointer" }}
            onClick={handleClose}
          >
            {t("forgotPassword")}
          </Link>

          <LoadingButton
            onClick={onSubmit}
            sx={{ my: 3 }}
            loading={loading}
            variant="contained"
            loadingPosition="start"
            startIcon={<LoginIcon />}
          >
            {t("Login")}
          </LoadingButton>

          {googleClientId && (
            <>
              <Divider sx={{ color: "secondary.main", mb: 2 }}>
                <Typography variant="caption" color="secondary">
                  {t("or")}
                </Typography>
              </Divider>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight={44}
                mb={2}
                gap={1}
              >
                {googleLoading && (
                  <Typography color="secondary">
                    {t("procesingAcces")}
                  </Typography>
                )}
                {!googleLoading && (
                  <LoadingButton
                    onClick={
                      googleButtonError ? retryGoogleLoad : requestGoogleAccess
                    }
                    loading={googleLoading}
                    startIcon={<GoogleLogo />}
                    variant="contained"
                    disabled={!googleReady && !googleButtonError}
                    sx={{
                      width: 320,
                      borderRadius: 999,
                      bgcolor: "#fff",
                      border: "1px solid #dadce0",
                      color: "#3c4043",
                      fontWeight: 600,
                      textTransform: "none",
                      boxShadow: "none",
                      opacity: 1,
                      "&:hover": {
                        bgcolor: "#f8fafd",
                        borderColor: "#d2e3fc",
                        boxShadow: "0 1px 2px rgba(60,64,67,.25)",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "rgba(255,255,255,0.72)",
                        color: "#5f6368",
                        border: "1px solid rgba(218,220,224,0.7)",
                      },
                    }}
                  >
                    {googleReady
                      ? t("continueWithGoogle")
                      : googleButtonError
                      ? t("retryGoogle")
                      : t("googleLoading")}
                  </LoadingButton>
                )}
                {googleButtonError && !googleReady && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    textAlign="center"
                    maxWidth={320}
                  >
                    {t("googleLoginError")}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {showGoogleConfigWarning && (
            <Typography color="warning.main" textAlign="center" mb={2}>
              {t("googleClientIdMissing")}
            </Typography>
          )}

          <Box my={2}>
            <Typography variant="body1" color="secondary" fontWeight={500}>
              {t("noAccount")}
            </Typography>
            <Typography
              component="a"
              color="primary"
              target="_blank"
              referrerPolicy="no-referrer"
              href="https://tradeasy.tech/registro-tradeasy2/"
            >
              {t("signupHere")}
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default Login;
